import {
  Package,
  CheckCircle,
  XCircle,
  Truck,
  MessageCircle,
  Wallet,
  ShieldCheck,
  ShieldOff,
  BellRing,
} from 'lucide-react';

const CHAT_TYPES = new Set([
  'chat',
  'chat_message',
  'message',
  'new_message',
]);

export const formatRelativeTime = timestamp => {
  if (!timestamp) return 'moments ago';
  const now = Date.now();
  const diff = now - timestamp;

  if (diff < 60 * 1000) return 'Just now';
  if (diff < 60 * 60 * 1000) {
    const minutes = Math.floor(diff / (60 * 1000));
    return `${minutes}m ago`;
  }
  if (diff < 24 * 60 * 60 * 1000) {
    const hours = Math.floor(diff / (60 * 60 * 1000));
    return `${hours}h ago`;
  }
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  return `${days}d ago`;
};

export const getNotificationIcon = type => {
  switch (type) {
    case 'new_request':
    case 'request_sent':
    case 'new_ride_request':
      return { Icon: Package, color: 'text-blue-600', bg: 'bg-blue-100' };
    case 'request_accepted':
    case 'ride_request_accepted':
      return { Icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' };
    case 'request_rejected':
    case 'ride_request_rejected':
      return { Icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' };
    case 'delivery_completed':
    case 'package_delivered':
    case 'ride_request_completed':
      return { Icon: Truck, color: 'text-orange-600', bg: 'bg-orange-100' };
    case 'ride_request_cancelled':
      return { Icon: XCircle, color: 'text-gray-600', bg: 'bg-gray-100' };
    case 'new_message':
      return { Icon: MessageCircle, color: 'text-purple-600', bg: 'bg-purple-100' };
    case 'payment_received':
    case 'wallet_transaction':
      return { Icon: Wallet, color: 'text-emerald-600', bg: 'bg-emerald-100' };
    case 'verification_approved':
      return { Icon: ShieldCheck, color: 'text-green-600', bg: 'bg-green-100' };
    case 'verification_rejected':
      return { Icon: ShieldOff, color: 'text-red-600', bg: 'bg-red-100' };
    default:
      return { Icon: BellRing, color: 'text-gray-600', bg: 'bg-gray-100' };
  }
};

export const normalizeNotification = doc => {
  const data = doc.data() || {};
  const createdAt = toMillis(data.createdAt);
  const readAt = toMillis(data.readAt);
  return {
    id: doc.id,
    ...data,
    data: data.data || {},
    createdAt,
    readAt,
  };
};

const toMillis = value => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (value.seconds) {
    return value.seconds * 1000 + (value.nanoseconds || 0) / 1000000;
  }
  if (value.toDate && typeof value.toDate === 'function') {
    return value.toDate().getTime();
  }
  return null;
};

export const shouldDisplayNotification = notification => {
  if (!notification) return false;
  const type = (notification.type || notification.data?.type || '').toLowerCase();
  const hasChatKeys = Boolean(
    notification?.data?.chatId || notification?.data?.conversationId,
  );
  return !hasChatKeys && !CHAT_TYPES.has(type);
};

export const sortNotifications = notifications =>
  [...notifications].sort(
    (a, b) => (b.createdAt || 0) - (a.createdAt || 0),
  );

export const notificationsFromSnapshot = snapshot =>
  sortNotifications(
    snapshot.docs
      .map(normalizeNotification)
      .filter(shouldDisplayNotification),
  );

// Clean tripId from URL encoding issues
const cleanTripId = (tripId) => {
  if (!tripId) return null;
  // Remove any trailing dashes or encoding artifacts
  return String(tripId).replace(/--+$/, '').trim();
};

export const navigateWithNotification = (notification, navigate) => {
  if (!notification || !navigate) return;
  const { type, data = {} } = notification;
  // Mobile tarafında olduğu gibi, asıl tip çoğunlukla data.type içinde geliyor
  const baseType = type || data.type;
  const tripType = data.tripType;
  const rawTripId = data.tripId;
  const tripId = cleanTripId(rawTripId);
  const requestId = data.requestId;
  const userRole = data.userRole;
  const listingId = data.listingId;
  const listingType = data.listingType;

  switch (baseType) {
    case 'new_request':
    case 'request_sent':
      if (tripId) {
        // For trip owners, navigate based on trip type
        if (userRole === 'tripOwner') {
          if (tripType === 'carrying') {
            navigate(`/traveler/trip-requests/${tripId}`);
          } else {
            navigate(`/trip-requests/${tripId}`);
          }
        } else {
          // For requesters, navigate to their requests page
          if (tripType === 'shipping') {
            navigate('/my-requests');
          } else {
            navigate('/traveler/my-requests');
          }
        }
      }
      break;
    case 'request_accepted':
    case 'request_rejected':
    case 'trip_cancellation_requested':
      if (userRole === 'requester') {
        if (tripType === 'shipping') {
          navigate('/my-requests');
        } else {
          navigate('/traveler/my-requests');
        }
      } else if (userRole === 'tripOwner' && tripId) {
        navigate(tripType === 'carrying' ? `/traveler/trip-requests/${tripId}` : `/trip-requests/${tripId}`);
      }
      break;
    case 'delivery_completed':
    case 'package_delivered':
      if (userRole === 'requester') {
        navigate(tripType === 'shipping' ? '/my-requests' : '/traveler/my-requests');
      } else if (userRole === 'tripOwner' && tripId) {
        navigate(tripType === 'carrying' ? `/traveler/trip-requests/${tripId}` : `/trip-requests/${tripId}`);
      }
      break;
    // Flexy Ride - web equivalents of the mobile ride navigation
    case 'new_ride_request':
      // Listing owner receives a new application for their ride listing
      if (listingId && userRole === 'listingOwner') {
        if (listingType === 'passenger') {
          // Passenger listing owner sees requests for this listing
          navigate(`/flexy-ride/passenger-listing-requests/${listingId}`);
        } else {
          // Default to seat listing requests
          navigate(`/flexy-ride/seat-listing-requests/${listingId}`);
        }
      }
      break;
    case 'ride_request_accepted':
    case 'ride_request_rejected':
    case 'ride_request_cancelled':
    case 'ride_request_completed':
      if (userRole === 'applicant') {
        // Applicant view:
        // - If applied to seat (driver) listing → My Requests
        // - If applied to passenger listing → My Applications
        if (listingType === 'seat') {
          navigate('/flexy-ride/my-requests');
        } else {
          navigate('/flexy-ride/my-applications');
        }
      } else if (userRole === 'listingOwner' && listingId) {
        // Listing owner:
        // - Passenger listing owner → their passenger listing requests
        // - Seat listing owner → seat listing requests
        if (listingType === 'passenger') {
          navigate('/flexy-ride/my-requests');
        } else {
          navigate(`/flexy-ride/seat-listing-requests/${listingId}`);
        }
      }
      break;
    case 'new_message':
      if (data.conversationId || data.chatId) {
        navigate(`/chat/${data.conversationId || data.chatId}`);
      } else {
        navigate('/chat');
      }
      break;
    case 'payment_received':
    case 'wallet_transaction':
      navigate('/my-requests');
      break;
    case 'verification_approved':
    case 'verification_rejected':
      navigate('/');
      break;
    default:
      break;
  }
};


